import React, { useEffect, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

import {
    EllipsisVertical,
    EyeIcon,
    Pencil,
    Trash2,
    Plus,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import { useCustomNotification } from "components/Toasts/OpenNotification";

import {
    fetchCommissionConfigurationsAction,
    deleteCommissionConfigurationAction,
} from "state/actions/agentCommissionActions";

import AddCommissionConfiguration from "./components/AddCommissionConfiguration";
import ViewCommissionConfiguration from "./components/ViewCommissionConfiguration";
import DeleteCommissionConfiguration from "./components/DeleteCommissionConfiguration";

const AgentCommissionConfiguration = () => {
    const dispatch = useDispatch();

    const notify = useCustomNotification();

    const {
        configurations = [],
        loading,
        error,
    } = useSelector(
        (state) => state.agentCommission
    );

    const [
        addDrawerOpen,
        setAddDrawerOpen,
    ] = useState(false);

    const [
        viewDrawerOpen,
        setViewDrawerOpen,
    ] = useState(false);

    const [
        deleteModalOpen,
        setDeleteModalOpen,
    ] = useState(false);

    const [
        selectedConfiguration,
        setSelectedConfiguration,
    ] = useState(null);

    const [
        startInEditMode,
        setStartInEditMode,
    ] = useState(false);

    useEffect(() => {
        dispatch(
            fetchCommissionConfigurationsAction()
        );
    }, [dispatch]);

    const handleView = (configuration) => {
        setSelectedConfiguration(configuration);
        setStartInEditMode(false);
        setViewDrawerOpen(true);
    };

    const handleEdit = (configuration) => {
        setSelectedConfiguration(configuration);
        setStartInEditMode(true);
        setViewDrawerOpen(true);
    };

    const handleDelete = (configuration) => {
        setSelectedConfiguration(configuration);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedConfiguration?.configuration_id) {
            return;
        }

        const response = await dispatch(
            deleteCommissionConfigurationAction(
                selectedConfiguration.configuration_id
            )
        );

        if (response?.success) {
            notify({
                message:
                    "Commission configuration deleted successfully.",
                type: "success",
            });

            setDeleteModalOpen(false);
            setSelectedConfiguration(null);
        } else {
            notify({
                message:
                    "Failed to delete commission configuration.",
                description: response?.error,
                type: "error",
            });
        }
    };

    const columnsData = [
        {
            Header: "Commission",
            accessor: "commission_percentage",
        },
        {
            Header: "Status",
            accessor: "status",
        },
        {
            Header: "Created",
            accessor: "created_at",
        },
        {
            Header: "Updated",
            accessor: "updated_at",
        },
        {
            Header: "Actions",
            accessor: "actions",
        },
    ];

    const tableData = configurations.map(
        (configuration) => ({
            commission_percentage: (
                <Text
                    fontWeight="600"
                    fontSize="sm"
                >
                    {configuration.commission_percentage}%
                </Text>
            ),

            status: (
                <StatusBadge
                    label={
                        configuration.is_active
                            ? "Active"
                            : "Inactive"
                    }
                    variant={getStatusVariant(
                        configuration.is_active
                            ? "Active"
                            : "Inactive"
                    )}
                />
            ),

            created_at: (
                <Text
                    fontSize="sm"
                    color="gray.600"
                >
                    {configuration.created_at
                        ? new Date(
                              configuration.created_at
                          ).toLocaleDateString()
                        : "-"}
                </Text>
            ),

            updated_at: (
                <Text
                    fontSize="sm"
                    color="gray.600"
                >
                    {configuration.updated_at
                        ? new Date(
                              configuration.updated_at
                          ).toLocaleDateString()
                        : "-"}
                </Text>
            ),

            actions: (
                <ThreeDotMenu
                    icon={
                        <EllipsisVertical
                            size={20}
                            color="grey"
                        />
                    }
                    items={[
                        {
                            icon: <EyeIcon size={16} />,
                            text: "View/Edit Configuration",
                            onClick: () =>
                                handleView(
                                    configuration
                                ),
                        },
                       
                        {
                            icon: <Trash2 size={16} />,
                            text: "Delete Configuration",
                            onClick: () =>
                                handleDelete(
                                    configuration
                                ),
                        },
                    ]}
                />
            ),
        })
    );

    return (
        <Flex
            direction="column"
            width="100%"
            p={6}
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
                mb={6}
            >
              

                <GradientButton
                    onClick={() =>
                        setAddDrawerOpen(true)
                    }
                >
                    <Flex
                        alignItems="center"
                        gap={2}
                    >
                        <Plus size={16} />
                        Add Configuration
                    </Flex>
                </GradientButton>
            </Flex>

            <ReusableTable
                columnsData={columnsData}
                tableData={tableData}
                isLoading={loading}
                error={error}
                emptyMessage="No commission configurations found."
            />

            <AddCommissionConfiguration
                isOpen={addDrawerOpen}
                onClose={() =>
                    setAddDrawerOpen(false)
                }
            />

            <ViewCommissionConfiguration
                isOpen={viewDrawerOpen}
                onClose={() => {
                    setViewDrawerOpen(false);
                    setStartInEditMode(false);
                    setSelectedConfiguration(null);
                }}
                configuration={selectedConfiguration}
                startInEditMode={startInEditMode}
            />

            <DeleteCommissionConfiguration
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setSelectedConfiguration(null);
                }}
                onConfirm={handleConfirmDelete}
                configuration={
                    selectedConfiguration
                }
            />
        </Flex>
    );
};

export default AgentCommissionConfiguration;